import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap, switchMap, map } from "rxjs/operators";
import { Response as AutoscoreResponse } from "../interfaces/autoscore.interface";

@Injectable()
export class PersonsService {
  constructor(private http: HttpClientService) {}

  public getPerson(id: string | number): Observable<any> {
    return this.http.get(`/api/persons/${id}`).pipe(tap((_) => console.log("PersonsService.getPerson: Ok.")));
  }

  public combine(id, combine_id): Observable<any> {
    const options = {
      params: { id, combine_id },
    };
    return this.http.get("/api/persons/combine", options).pipe(tap((_) => console.log("PersonsService.combine: Ok.")));
  }

  public findPersonByEmail(email: string): Observable<any> {
    const options = {
      params: { email },
    };
    return this.http
      .get("/api/persons", options)
      .pipe(tap((_) => console.log("PersonsService.findPersonByEmail: Ok.")));
  }

  public addPerson({
    first_name = undefined,
    middle_name = undefined,
    first_last_name = undefined,
    second_last_name = undefined,
    rfc = undefined,
    fecha_nacimiento = undefined,
    regime = undefined,
    phones = undefined,
    emails = undefined,
    client_id,
    relation = undefined,
    ingresos_comprobables = undefined,
    expenses = undefined,
    curp = undefined,
  }): Observable<any> {
    const params = {
      primer_nombre: first_name,
      segundo_nombre: middle_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      rfc,
      fecha_nacimiento,
      phones,
      emails,
      relation,
      regime,
      ingresos_comprobables,
      expenses,
      curp,
    };
    return this.http
      .post(`/api/contact/${client_id}/persons/create_person`, params)
      .pipe(tap((_) => console.log("PersonsService.addPerson: Ok.")));
  }

  public getPostalCode(postal_code: string, closest = false): Observable<any> {
    const options = {
      params: { closest: closest.toString() },
    };
    return this.http
      .get(`/api/postal_codes/${postal_code}`, options)
      .pipe(tap((_) => console.log("PersonsService.getPostalCode: Ok.")));
  }
  public notifyBuroResult(buro_id): Observable<any> {
    return this.http
      .post(`/api/consultas_buros/${buro_id}/notify_result`, {})
      .pipe(tap((_) => console.log("PersonsService.notifyBuroResult: Ok.")));
  }

  public getBuroStatus(buro_id): Observable<any> {
    return this.http
      .get(`/api/consultas_buros/${buro_id}/status`)
      .pipe(tap((_) => console.log("PersonsService.getBuroStatus: Ok.")));
  }

  public addBuroAsync(
    id,
    first_name,
    middle_name,
    first_last_name,
    second_last_name,
    rfc,
    fecha_nacimiento,
    calle,
    numero,
    codigo_postal,
    colonia,
    tiene_credito_automotriz,
    tiene_hipoteca,
    tiene_tarjeta_credito,
    municipio_delegacion,
    ultimos_digitos_tarjeta_credito,
    estado,
    ciudad
  ): Observable<any> {
    return this.getPostalCode(codigo_postal, true).pipe(
      map((postalCode) => postalCode.codigo_postal),
      switchMap((postalCode) => {
        const params = {
          id,
          primer_nombre: first_name,
          segundo_nombre: middle_name,
          primer_apellido: first_last_name,
          segundo_apellido: second_last_name,
          rfc,
          fecha_nacimiento,
          calle,
          numero,
          codigo_postal: postalCode.toString().padStart(5, "0"),
          colonia,
          tiene_credito_automotriz,
          tiene_hipoteca,
          tiene_tarjeta_credito,
          municipio_delegacion,
          ultimos_digitos_tarjeta_credito,
          estado,
          ciudad,
        };
        return this.http
          .post(`/api/persons/${id}/consultas_buros/create_async`, params)
          .pipe(tap((_) => console.log("PersonsService.addBuroAsync: Ok.")));
      })
    );
  }

  public addBuro(
    id,
    first_name,
    middle_name,
    first_last_name,
    second_last_name,
    rfc,
    fecha_nacimiento,
    calle,
    numero,
    codigo_postal,
    colonia,
    tiene_credito_automotriz,
    tiene_hipoteca,
    tiene_tarjeta_credito,
    municipio_delegacion,
    ultimos_digitos_tarjeta_credito,
    estado,
    ciudad
  ): Observable<any> {
    return this.getPostalCode(codigo_postal, true).pipe(
      map((postalCode) => postalCode.codigo_postal),
      switchMap((postalCode) => {
        const params = {
          id,
          primer_nombre: first_name,
          segundo_nombre: middle_name,
          primer_apellido: first_last_name,
          segundo_apellido: second_last_name,
          rfc,
          fecha_nacimiento,
          calle,
          numero,
          codigo_postal: postalCode.toString().padStart(5, "0"),
          colonia,
          tiene_credito_automotriz,
          tiene_hipoteca,
          tiene_tarjeta_credito,
          municipio_delegacion,
          ultimos_digitos_tarjeta_credito,
          estado,
          ciudad,
        };
        return this.http
          .post(`/api/persons/${id}/consultas_buros`, params)
          .pipe(tap((_) => console.log("PersonsService.addBuro: Ok.")));
      })
    );
  }

  public checkViability(person_id) {
    return this.http
      .get(`/api/persons/${person_id}/leasings/viable`)
      .pipe(tap((_) => console.log("PersonsService.checkViability: Ok.")));
  }

  public updatePerson({
    first_name = undefined,
    middle_name = undefined,
    first_last_name = undefined,
    second_last_name = undefined,
    rfc = undefined,
    fecha_nacimiento = undefined,
    regime = undefined,
    phones = undefined,
    emails = undefined,
    client_id,
    relation = undefined,
    ingresos_comprobables = undefined,
    expenses = undefined,
    person_id,
    curp = undefined,
    banned = undefined,
  }): Observable<any> {
    const params = {
      primer_nombre: first_name,
      segundo_nombre: middle_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      rfc,
      fecha_nacimiento,
      phones,
      emails,
      relation,
      regime,
      ingresos_comprobables,
      expenses,
      person_id,
      curp,
      banned,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] == undefined) {
        delete params[key];
      }
    });

    return this.http
      .post(`/api/contact/${client_id}/persons/update_person`, params)
      .pipe(tap((_) => console.log("PersonsService.updatePerson: Ok.")));
  }

  public createRelation(id, person_id, relation): Observable<any> {
    const params = {
      person_id,
      relation,
    };
    return this.http
      .post(`/api/contact/${id}/persons/create_relation`, params)
      .pipe(tap((_) => console.log("PersonsService.createRelation: Ok.")));
  }

  public updatePersonField(person_id, params): Observable<any> {
    return this.http
      .post(`/api/contact/${person_id}/persons/update_person_contactable`, params)
      .pipe(tap((_) => console.log("PersonsService.updatePersonField: Ok.")));
  }

  public updateContactInfo(person_id, params): Observable<any> {
    return this.http
      .post(`/api/persons/${person_id}/update_contact_info`, params)
      .pipe(tap((_) => console.log("PersonsService.updatContactInfo: Ok.")));
  }

  public update({
    first_name = undefined,
    middle_name = undefined,
    first_last_name = undefined,
    second_last_name = undefined,
    rfc = undefined,
    fecha_nacimiento = undefined,
    regime = undefined,
    phones = undefined,
    emails = undefined,
    relation = undefined,
    ingresos_comprobables = undefined,
    numero_telefono = undefined,
    expenses = undefined,
    person_id,
  }): Observable<any> {
    const params = {
      primer_nombre: first_name,
      segundo_nombre: middle_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      rfc,
      fecha_nacimiento,
      phones,
      emails,
      relation,
      regime,
      ingresos_comprobables,
      expenses,
      person_id,
      numero_telefono,
    };
    return this.http
      .put(`/api/persons/${person_id}`, params)
      .pipe(tap((_) => console.log("PersonsService.update: Ok.")));
  }

  public postQuote(person_id, quote): Observable<any> {
    return this.http
      .post(`/api/persons/${person_id}/quotes`, quote)
      .pipe(tap((_) => console.log("PersonsService.postQuote")));
  }

  public getQuotes(id: string): Observable<any> {
    return this.http.get(`/api/persons/${id}/quotes`).pipe(tap((_) => console.log("PersonsService.getQuotes")));
  }

  public getClientInfo(id: string): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/client_info`)
      .pipe(tap((_) => console.log("PersonsService.getClientInfo: Ok.")));
  }

  public getContact(id: string): Observable<any> {
    return this.http.get(`/api/persons/${id}/contact`).pipe(tap((_) => console.log("PersonsService.getContact: Ok.")));
  }

  public saveBuroWithNip(params: {
    person_id;
    primer_nombre;
    segundo_nombre;
    primer_apellido;
    segundo_apellido;
    fecha_nacimiento;
    rfc;
    calle;
    numero;
    interior;
    colonia;
    municipio_delegacion;
    estado;
    codigo_postal;
  }): Observable<any> {
    return this.http
      .post(`/api/persons/${params.person_id}/consultas_buros/create_validation_nip`, params)
      .pipe(tap((_) => console.log("PersonsService.saveBuroWithNip: Ok.")));
  }

  public validateNip({ consulta_buro_id, nip }): Observable<any> {
    return this.http
      .post(`/api/consultas_buros/${consulta_buro_id}/validate_nip`, { nip })
      .pipe(tap((_) => console.log("PersonsService.validateNip: Ok.")));
  }

  public resendNip(person_id, nip, user_id, emails: any[], phone_numbers: any[], person_titular_id): Observable<any> {
    const params = {
      nip,
      user_id,
      emails,
      phone_numbers,
      person_titular_id,
    };
    return this.http
      .post(`/api/persons/${person_id}/consultas_buros/resend_nip`, params)
      .pipe(tap((_) => console.log("PersonsService.resendNip: Ok.")));
  }

  public sendNotificationAuthorizedClient(person_id, user_id): Observable<any> {
    const params = {
      user_id,
    };
    return this.http
      .post(`/api/persons/${person_id}/consultas_buros/notification_nip_success`, params)
      .pipe(tap((_) => console.log("PersonsService.sendNotificationAuthorizedClient: Ok.")));
  }

  public getLastQuote(person_id): Observable<any> {
    return this.http
      .get(`/api/persons/${person_id}/quotes/last`)
      .pipe(tap((_) => console.log("PersonsService.getLastQuote: Ok.")));
  }

  public indexBuro(person_id, user_id): Observable<any> {
    const options = {
      params: {
        person_id,
        usuario_id: user_id,
      },
    };
    return this.http
      .get(`/api/persons/${person_id}/consultas_buros`, options)
      .pipe(tap((_) => console.log("PersonsService.indexBuro: Ok.")));
  }

  public getAutoscore(person_id: number | string): Observable<AutoscoreResponse> {
    return this.http
      .get(`/api/persons/${person_id}/auto_score`)
      .pipe(tap((_) => console.log("PersonsService.getAutoscore: Ok.")));
  }

  public getBuro(person_id, user_id): Observable<any> {
    const options = {
      params: {
        usuario_id: user_id,
      },
    };
    return this.http
      .get(`/api/persons/${person_id}/consultas_buros/datos_buro`, options)
      .pipe(tap((_) => console.log("PersonsService.getBuro: Ok.")));
  }

  public putSolicitud(id, quote): Observable<any> {
    return this.http
      .put(`/api/persons/${id}/quotes/update_last`, quote)
      .pipe(tap((_) => console.log("PersonsService.putSolicitud")));
  }

  public getEmail(email, contactable_type): Observable<any> {
    const options = {
      params: {
        email,
        "contactable_types[]": contactable_type,
      },
    };
    return this.http.get("/api/email", options).pipe(tap((_) => console.log("PersonsService.getEmail")));
  }

  public setActivePhone(phone): Observable<any> {
    const options = {
      active: phone.active,
    };
    return this.http
      .put(`/api/persons/${phone.contactable_id}/phone_number/${phone.id}`, options)
      .pipe(tap((_) => console.log("PersonService.setActivePhone: Ok.")));
  }

  public setPrincipalPhone(phone): Observable<any> {
    const options = {};
    return this.http
      .put(`/api/persons/${phone.contactable_id}/phone_number/${phone.id}/principal_phone_number`, options)
      .pipe(tap((_) => console.log("PersonService.setPrincipalPhone: Ok.")));
  }

  public setActiveMail(mail): Observable<any> {
    const options = {
      active: mail.active,
    };
    return this.http
      .put(`/api/persons/${mail.contactable_id}/email/${mail.id}`, options)
      .pipe(tap((_) => console.log("PersonService.setActiveMail: Ok.")));
  }

  public setPrincipalMail(mail): Observable<any> {
    const options = {};
    return this.http
      .put(`/api/persons/${mail.contactable_id}/email/${mail.id}/principal_email`, options)
      .pipe(tap((_) => console.log("PersonService.setPrincipalMail: Ok.")));
  }

  public duplicated({
    first_name = "",
    first_last_name = "",
    birthday = "",
    second_last_name = "",
    source_channel = undefined,
    prevent_change = false,
    id = undefined,
    curp = undefined,
    contact_id = undefined,
  } = {}): Observable<any> {
    const body = {
      primer_nombre: first_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      fecha_nacimiento: birthday,
      source_channel,
      prevent_change,
      id,
      curp,
      contact_id,
    };
    return this.http.post("/api/persons/duplicated", body).pipe(tap((_) => console.log("PersonsService.duplicated")));
  }

  public getVeridoc(id, full_response = true): Observable<any> {
    return this.http
      .get(`/api/validation_processes/${id}/veridoc?full_response=${full_response}`)
      .pipe(tap((_) => console.log("PersonsService.getVeridoc")));
  }

  public getAddressValidationProcess(id): Observable<any> {
    return this.http
      .get(`/api/validation_processes/${id}/address`)
      .pipe(tap((_) => console.log("PersonsService.getAddressValidationProcess: Ok.")));
  }

  public postAddressValidationProcess(addressData, id): Observable<any> {
    const body = {
      street: addressData.street,
      interior_number: addressData.interiorNumber,
      exterior_number: addressData.exteriorNumber,
      postal_code: addressData.postalCode,
      sublocality: addressData.sublocality,
      municipality: addressData.municipality,
      state: addressData.state,
      longitud: addressData.longitud,
      latitud: addressData.latitud,
    };
    return this.http
      .post(`/api/validation_processes/${id}/address`, body)
      .pipe(tap((_) => console.log("PersonsService.postAddressValidationProcess: Ok.")));
  }

  public getPersonBySlug(slug): Observable<any> {
    const options = {
      params: { slug },
    };
    return this.http
      .get("/api/persons/sponsor", options)
      .pipe(tap((_) => console.log("PersonsService.getPersonBySlug: Ok.")));
  }

  public getReferences(id): Observable<any> {
    return this.http
      .get(`/api/validation_processes/${id}/references`)
      .pipe(tap((_) => console.log("PersonsService.getReferences: Ok.")));
  }

  public postReferences(id, references): Observable<any> {
    return this.http
      .post(`/api/validation_processes/${id}/references`, references)
      .pipe(tap((_) => console.log("PersonsService.postReferences")));
  }

  public putReference(id, reference_id, reference): Observable<any> {
    return this.http
      .put(`/api/validation_processes/${id}/references/${reference_id}`, reference)
      .pipe(tap((_) => console.log("PersonsService.putReference")));
  }

  public create(person: any, sponsorId?: number | string | null, owner?: string): Observable<any> {
    const params = Object.assign({}, person, { sponsor_id: sponsorId, owner });
    return this.http.post("/api/persons", params).pipe(tap((_) => console.log("PersonsService.create")));
  }

  public nextCall(id, activity_id): Observable<any> {
    const options = { params: { activity_id } };
    return this.http
      .get(`/api/persons/${id}/next_call`, options)
      .pipe(tap((_) => console.log("PersonsService.nextCall: Ok.")));
  }

  public contacts(id): Observable<any> {
    return this.http.get(`/api/persons/${id}/contacts`).pipe(tap((_) => console.log("PersonsService.contacts: Ok.")));
  }

  public bulk_info(ids: number[], external_quote_ids?: string[]): Observable<any> {
    return this.http
      .post("/api/persons/bulk_info", { person_ids: ids, external_quote_ids })
      .pipe(tap((_) => console.log("PersonsService.bulk_info: Ok.")));
  }

  public getBillingData(ids): Observable<any> {
    const options = { params: { ids } };
    return this.http
      .get("/api/persons/billing_info", options)
      .pipe(tap((_) => console.log("PersonsService.getBillingData: Ok.")));
  }

  public getContactData(id): Observable<any> {
    const options = { params: { id } };
    return this.http
      .get("/api/persons/contact_info", options)
      .pipe(tap((_) => console.log("PersonsService.getContactData: Ok.")));
  }

  public getPersonsByFilters(filter, search): Observable<any> {
    const options = {
      params: {
        filter,
        search,
      },
    };
    return this.http
      .get("/api/persons/person_by_filters", options)
      .pipe(tap((_) => console.log("PersonsService.getPersonsByFilters: Ok.")));
  }

  public getAnticipatedPaymentsData(id): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/person_anticipated_info`, null)
      .pipe(tap((_) => console.log("PersonsService.getAnticipatedPaymentsData: Ok.")));
  }

  public notifyAnticipatedPayments(notifyparams: any): Observable<any> {
    const options = {
      params: notifyparams,
    };
    return this.http
      .get("/api/persons/notify_anticipated_payments", options)
      .pipe(tap((_) => console.log("PersonsService.notifyAnticipatedPayments: Ok.")));
  }

  public getContactDataAllName(id): Observable<any> {
    const options = { params: { id } };
    return this.http
      .get("/api/persons/contact_info_all_names", options)
      .pipe(tap((_) => console.log("PersonsService.getContactDataAllName: Ok.")));
  }

  public collection_info(ids): Observable<any> {
    const options = { params: { ids } };
    return this.http
      .get("/api/persons/collection_info", options)
      .pipe(tap((_) => console.log("PersonsService.collection_info: Ok.")));
  }

  public collectionContactInfo(ids, account_id?: number): Observable<any> {
    const params: any = { ids };
    if (account_id) {
      params.account_id = account_id;
    }
    const options = { params };
    return this.http
      .get("/api/persons/collection_contact_info", options)
      .pipe(tap((_) => console.log("PersonsService.collectionContactInfo: Ok.")));
  }

  public createAddress(addressData, addressable_id, addressable_type, force_create = false): Observable<any> {
    const body = {
      street: addressData.street,
      interior_number: addressData.interiorNumber,
      exterior_number: addressData.exteriorNumber,
      postal_code: addressData.postalCode,
      sublocality: addressData.sublocality,
      municipality: addressData.municipality,
      state: addressData.state,
      longitud: addressData.longitud,
      latitud: addressData.latitud,
      google_maps_link: addressData.google_maps_link || null,
      addressable_id,
      addressable_type,
      force_create,
    };
    return this.http.post("/api/address", body).pipe(tap((_) => console.log("PersonsService.createAddress: Ok.")));
  }

  public setMainAddress(person_id: number, address_id: number): Observable<any> {
    return this.http
      .patch(`/api/persons/${person_id}/addresses/${address_id}/set_main`, {})
      .pipe(tap((_) => console.log("PersonsService.setMainAddress: Ok.")));
  }

  public deleteFacadePhoto(person_id: number, address_id: number, photo_id: number): Observable<any> {
    return this.http
      .delete(`/api/persons/${person_id}/addresses/${address_id}/facade_photos/${photo_id}`)
      .pipe(tap((_) => console.log("PersonsService.deleteFacadePhoto: Ok.")));
  }

  // POST a new note for an address. Backend validates that the address belongs to the person
  // and that body is non-blank.
  public createAddressNote(person_id: number, address_id: number, body: string): Observable<any> {
    return this.http
      .post(`/api/persons/${person_id}/addresses/${address_id}/notes`, { body })
      .pipe(tap((_) => console.log("PersonsService.createAddressNote: Ok.")));
  }

  public updateAddress(person_id: number, address_id: number, addressData): Observable<any> {
    const body = {
      street: addressData.street,
      interior_number: addressData.interiorNumber,
      exterior_number: addressData.exteriorNumber,
      postal_code: addressData.postalCode,
      sublocality: addressData.sublocality,
      municipality: addressData.municipality,
      state: addressData.state,
      longitud: addressData.longitud,
      latitud: addressData.latitud,
      google_maps_link: addressData.google_maps_link || null,
    };
    return this.http
      .patch(`/api/persons/${person_id}/addresses/${address_id}`, body)
      .pipe(tap((_) => console.log("PersonsService.updateAddress: Ok.")));
  }

  public postRemindSelectQuoteEvent(id) {
    return this.http
      .post(`/api/persons/${id}/remind_select_quote_event`, null)
      .pipe(tap((_) => console.log("PersonsService.remindSelectQuoteEvent: Ok.")));
  }

  public postFinishOnlineRegistration(id) {
    return this.http
      .post(`/api/persons/${id}/finish_online_registration`, null)
      .pipe(tap((_) => console.log("PersonsService.postFinishOnlineRegistration: Ok.")));
  }

  public getApplicationsComments(id) {
    return this.http
      .get(`/api/persons/${id}/applications_comments`, null)
      .pipe(tap((_) => console.log("PersonsService.getApplicationsComments: Ok.")));
  }

  public getBulkPayments(ids): Observable<any> {
    const options = { ids };
    return this.http
      .post("/api/persons/bulk_payments", options)
      .pipe(tap((_) => console.log("PersonsService.getBulkPayments: Ok")));
  }

  public getByVin(vin): Observable<any> {
    const options = { vin };
    return this.http.post("/api/persons/vin", options).pipe(tap((_) => console.log("PersonsService.getByVin: Ok.")));
  }

  public getVehicles(id): Observable<any> {
    const options = { params: { id } };
    return this.http
      .get("/api/persons/vehicles", options)
      .pipe(tap((_) => console.log("PersonsService.getVehicles: Ok.")));
  }

  public getPaymentsId(id): Observable<any> {
    const options = { id };
    return this.http
      .post("/api/persons/payments", options)
      .pipe(tap((_) => console.log("PersonsService.getPaymentsId: Ok.")));
  }

  public getFraudReport(id): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/fraud_report`)
      .pipe(tap((_) => console.log("PersonsService.getFraudReport: Ok.")));
  }

  public downloadImssInfo(person_id: number): Observable<any> {
    return this.http
      .get(`/api/persons/${person_id}/download_imss_info`)
      .pipe(tap((_) => console.log("PersonsService.downloadImssInfo: Ok.")));
  }

  public references_applications(id: number): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/references_applications`)
      .pipe(tap((_) => console.log("PersonsService.references_applications: Ok.")));
  }

  public getPersonBasicInfo(id: string | number): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/basic_info`)
      .pipe(tap((_) => console.log("PersonsService.getPersonBasicInfo: Ok.")));
  }

  public getPersonIncomeType(id: string | number): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/income_type`)
      .pipe(tap((_) => console.log("PersonsService.getPersonIncomeType: Ok.")));
  }

  public getAddresses(id: string | number): Observable<any> {
    return this.http
      .get(`/api/persons/${id}/addresses`)
      .pipe(tap((_) => console.log("PersonsService.getAddresses: Ok.")));
  }
}
